import type { Map as MlMap, StyleSpecification, LayerSpecification } from "maplibre-gl";
import { BASEMAP_STYLE, BASEMAP_STYLE_LIGHT } from "./config";

/**
 * v0.9.9 B — light / dark mode.
 *  - default = the device's prefers-color-scheme; the viewer's choice is kept in localStorage
 *    (try/catch: private mode → just the default) and set on <html data-theme> before first paint
 *    (public/theme-init.js — the CSP blocks inline scripts)
 *  - basemap: OpenFreeMap dark ⇄ positron (same sprite, glyphs and sources) via setStyle with
 *    diff + transformStyle, so every one of our sources / layers / images stays, in the same order
 *  - our layers: map.addLayer / setPaintProperty are wrapped once; the colours each module writes
 *    are kept as the dark originals, and in light mode a fixed rule maps them (same hue = same
 *    meaning; labels → dark text on a white halo). Switching back writes the originals again.
 *  - canvas icons: modules listen for "fs-theme" and redraw their light / dark variant (same shapes).
 */
export type Theme = "dark" | "light";
const KEY = "fs-theme";

export function savedTheme(): Theme | null {
  try { const v = localStorage.getItem(KEY); return v === "light" || v === "dark" ? v : null; } catch { return null; }
}
export function systemTheme(): Theme {
  try { return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark"; } catch { return "dark"; }
}
let theme: Theme = savedTheme() ?? systemTheme();
export const currentTheme = (): Theme => theme;
export const isLight = (): boolean => theme === "light";
export const basemapFor = (t: Theme): string => (t === "light" ? BASEMAP_STYLE_LIGHT : BASEMAP_STYLE);

function applyDocument(t: Theme): void {
  const root = document.documentElement;
  root.dataset.theme = t;
  root.style.colorScheme = t;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", t === "light" ? "#f4f7fb" : "#05080f");
}
applyDocument(theme);

/* ── colour rule (dark original → light) ─────────────────────────────────────────────── */
type HSL = [number, number, number, number];
function parse(c: string): HSL | null {
  let r: number, g: number, b: number, a = 1;
  const s = c.trim().toLowerCase();
  if (s === "white") { r = g = b = 255; }
  else if (s === "black") { r = g = b = 0; }
  else if (s[0] === "#") {
    let h = s.slice(1);
    if (h.length === 3 || h.length === 4) h = [...h].map((x) => x + x).join("");
    if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/.test(h)) return null;
    r = parseInt(h.slice(0, 2), 16); g = parseInt(h.slice(2, 4), 16); b = parseInt(h.slice(4, 6), 16);
    if (h.length === 8) a = parseInt(h.slice(6, 8), 16) / 255;
  } else {
    const m = /^rgba?\(([^)]+)\)$/.exec(s); if (!m) return null;
    const p = m[1]!.split(/[\s,/]+/).filter(Boolean).map(Number);
    if (p.length < 3 || p.some((v) => Number.isNaN(v))) return null;
    [r, g, b] = p as [number, number, number]; a = p[3] ?? 1;
  }
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
  let h = 0, sat = 0;
  if (mx !== mn) {
    const d = mx - mn; sat = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    h = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4; h /= 6;
  }
  return [h, sat, l, a];
}
function fmt([h, s, l, a]: HSL): string {
  const k = (n: number): number => (n + h * 12) % 12, f = (n: number): number => l - s * Math.min(l, 1 - l) * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  const c = (v: number): number => Math.round(Math.max(0, Math.min(1, v)) * 255);
  return a < 1 ? `rgba(${c(f(0))},${c(f(8))},${c(f(4))},${+a.toFixed(3)})` : `rgb(${c(f(0))},${c(f(8))},${c(f(4))})`;
}
/** One colour, for one paint property, in light mode. */
export function lightColor(c: string, prop: string): string {
  const v = parse(c); if (!v) return c;
  const [h, s, l, a] = v;
  if (prop === "text-halo-color" || prop === "icon-halo-color") return fmt([0, 0, 1, Math.max(a, 0.92)]);
  if (prop === "text-color" || prop === "icon-color") {
    // dark text, same hue; the near-white "plain" labels become slate
    return s < 0.25 ? fmt([h, s * 0.6, 0.1 + (1 - l) * 0.15, a]) : fmt([h, Math.min(1, s), Math.min(l, 0.27), a]);
  }
  if (l < 0.16) return fmt([h, 0, 1, a]);                    // dark masks / halos → white
  if (s < 0.2 && l > 0.55) return fmt([h, s, 1.05 - l * 0.85, a]); // whites / light greys → slate
  return fmt([h, s, l > 0.42 ? Math.max(0.36, l * 0.78) : l, a]); // colours keep their hue, a bit deeper
}
function mapColors(v: unknown, prop: string): unknown {
  if (typeof v === "string") return /^(#|rgb|white$|black$)/i.test(v.trim()) ? lightColor(v, prop) : v;
  if (Array.isArray(v)) return v.map((x) => mapColors(x, prop));
  return v;
}
const isColorProp = (p: string): boolean => p.endsWith("-color");
export const themed = (v: unknown, prop: string): unknown => (theme === "light" && isColorProp(prop) ? mapColors(v, prop) : v);

/* v0.9.9a: per-layer light-mode paint chosen by hand (size / stroke / opacity, not only colour),
   and map labels get a real white halo (≥ 2 px) so the letters never sit on a coloured fill */
const lightOv = new Map<string, Record<string, unknown>>();
/** Hand-set light-mode paint for one of our layers (call before its addLayer). Dark keeps the module's own values. */
export function setLightPaint(id: string, paint: Record<string, unknown>): void { lightOv.set(id, paint); }
const tracked = (id: string, p: string): boolean => isColorProp(p) || p === "text-halo-width" || lightOv.get(id)?.[p] !== undefined;
function tv(id: string, p: string, v: unknown): unknown {
  if (theme !== "light") return v;
  const ov = lightOv.get(id)?.[p]; if (ov !== undefined) return ov;
  if (p === "text-halo-width") return typeof v === "number" ? Math.max(v, 2) : v;
  return themed(v, p);
}

/* ── page CSS: light overrides generated from the stylesheet itself ─────────────────────── */
const COLOR_RE = /#[0-9a-fA-F]{3,8}\b|rgba?\([^)]*\)/g;
const textProp = (p: string): boolean => p === "color" || p === "-webkit-text-fill-color" || p === "caret-color";
function vivid(v: string): boolean {
  return (v.match(COLOR_RE) ?? []).some((c) => { const h = parse(c); return !!h && h[1] > 0.35 && h[2] > 0.2 && h[2] < 0.8 && h[3] > 0.6; });
}
function lightDecl(prop: string, val: string, keepText: boolean): string | null {
  if (!COLOR_RE.test(val)) return null; COLOR_RE.lastIndex = 0;
  if (val.includes("var(")) return null;
  if (textProp(prop)) return keepText ? null : val.replace(COLOR_RE, (c) => lightColor(c, "text-color"));
  if (prop.includes("shadow")) return val.replace(COLOR_RE, (c) => { const h = parse(c); return h && h[2] < 0.16 ? fmt([h[0], h[1], h[2], h[3] * 0.35]) : lightColor(c, "fill-color"); });
  // an opaque white / pale surface (a badge, a light button) is already light → keep it
  return val.replace(COLOR_RE, (c) => { const h = parse(c); return h && h[1] < 0.2 && h[2] > 0.85 && h[3] >= 0.5 ? c : lightColor(c, "fill-color"); });
}
function lightSelector(sel: string): string {
  return sel.split(",").map((x) => {
    const t = x.trim();
    if (t.startsWith(":root")) return t.replace(":root", ':root[data-theme="light"]');
    if (/^html\b/.test(t)) return t.replace(/^html/, 'html[data-theme="light"]');
    return `:root[data-theme="light"] ${t}`;
  }).join(", ");
}
function lightRules(rules: CSSRuleList): string {
  let out = "";
  for (const r of Array.from(rules)) {
    if (r instanceof CSSMediaRule) { const inner = lightRules(r.cssRules); if (inner) out += `@media ${r.conditionText} {${inner}}`; continue; }
    if (r instanceof CSSSupportsRule) { const inner = lightRules(r.cssRules); if (inner) out += `@supports ${r.conditionText} {${inner}}`; continue; }
    if (!(r instanceof CSSStyleRule) || r.selectorText.includes("data-theme")) continue;
    if (/^\s*\.(maplibregl|mapboxgl)-/.test(r.selectorText)) continue; // MapLibre's own (already light) CSS
    const st = r.style;
    // white text on a coloured button / badge stays white (the colour behind it only gets deeper)
    const bg = st.getPropertyValue("background-color") + " " + st.getPropertyValue("background-image");
    const fg = parse((st.getPropertyValue("color").match(COLOR_RE) ?? [""])[0]!);
    const keepText = !!fg && fg[2] > 0.85 && vivid(bg);
    let decl = "";
    for (let i = 0; i < st.length; i++) {
      const p = st[i]!; if (p.startsWith("--")) continue;
      const v = lightDecl(p, st.getPropertyValue(p), keepText);
      if (v != null) decl += `${p}:${v}${st.getPropertyPriority(p) ? " !important" : ""};`;
    }
    if (decl) out += `${lightSelector(r.selectorText)}{${decl}}`;
  }
  return out;
}
let lightCssDone = false;
/** Build the light overrides once (from every same-origin stylesheet, i.e. ours). WebKit can run our
 *  module before the stylesheet <link> has parsed → nothing to read yet → try again once it has. */
export function ensureLightCss(): void {
  if (lightCssDone) return;
  let css = "";
  for (const sh of Array.from(document.styleSheets)) {
    try { if (sh.href && new URL(sh.href).origin !== location.origin) continue; css += lightRules(sh.cssRules); } catch { /* cross-origin sheet */ }
  }
  if (!css) {
    const retry = (): void => { if (theme === "light") ensureLightCss(); };
    document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]').forEach((l) => l.addEventListener("load", retry, { once: true }));
    window.addEventListener("load", retry, { once: true });
    window.setTimeout(retry, 300);
    return;
  }
  const el = document.createElement("style"); el.id = "fs-light-css"; el.textContent = css;
  document.head.append(el);
  lightCssDone = true;
}

/* ── inline colours written by our modules (card text, swatches) ───────────────────────── */
const INLINE_ORIG = "data-dark-style";
function themeInline(el: HTMLElement): void {
  const cur = el.getAttribute("style") ?? "";
  if (!/color|background|border/.test(cur)) return;
  const darkStyle = el.getAttribute(INLINE_ORIG) ?? cur;
  if (theme === "dark") { if (el.hasAttribute(INLINE_ORIG)) { el.setAttribute("style", darkStyle); el.removeAttribute(INLINE_ORIG); } return; }
  if (el.hasAttribute(INLINE_ORIG) && cur !== el.dataset.lightStyle) return; // rewritten by its module since → handled by the observer
  const lightStyle = darkStyle.split(";").map((d) => {
    const i = d.indexOf(":"); if (i < 0) return d;
    const p = d.slice(0, i).trim().toLowerCase(), v = d.slice(i + 1);
    const nv = lightDecl(p, v, p === "color" && vivid(darkStyle.replace(/(^|;)\s*color\s*:[^;]*/i, "")) && (parse((v.match(COLOR_RE) ?? [""])[0]!)?.[2] ?? 0) > 0.85);
    return nv == null ? d : `${d.slice(0, i)}:${nv}`;
  }).join(";");
  if (lightStyle === cur) return;
  el.setAttribute(INLINE_ORIG, darkStyle); el.dataset.lightStyle = lightStyle;
  el.setAttribute("style", lightStyle);
}
function themeInlineTree(root: ParentNode): void {
  if (root instanceof HTMLElement && root.hasAttribute("style")) themeInline(root);
  root.querySelectorAll<HTMLElement>("[style]").forEach(themeInline);
}
let observer: MutationObserver | null = null;
function watchInline(): void {
  if (observer) return;
  observer = new MutationObserver((muts) => {
    if (theme === "dark") return;
    for (const m of muts) {
      if (m.type === "attributes" && m.target instanceof HTMLElement) {
        const el = m.target, cur = el.getAttribute("style") ?? "";
        if (cur === el.dataset.lightStyle) continue; // our own write
        el.removeAttribute(INLINE_ORIG); delete el.dataset.lightStyle; themeInline(el);
      } else m.addedNodes.forEach((n) => { if (n instanceof HTMLElement) themeInlineTree(n); });
    }
  });
  observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["style"] });
}
/** Call once the page is built: light CSS + inline colours follow the mode from now on. */
export function startPageTheme(): void {
  if (theme === "light") ensureLightCss();
  watchInline();
  if (theme === "light") themeInlineTree(document.body);
}

/* ── map wiring ──────────────────────────────────────────────────────────────────────── */
const ours = new Set<string>();           // our layer ids (everything added through map.addLayer)
const oursSrc = new Set<string>();        // our source ids
const orig = new Map<string, Record<string, unknown>>(); // layer → dark colour originals
let mapRef: MlMap | null = null;

export function installTheme(map: MlMap): void {
  mapRef = map;
  const addLayer = map.addLayer.bind(map), removeLayer = map.removeLayer.bind(map);
  const addSource = map.addSource.bind(map), removeSource = map.removeSource.bind(map);
  const setPaint = map.setPaintProperty.bind(map);
  map.addLayer = ((layer: LayerSpecification & { paint?: Record<string, unknown> }, before?: string) => {
    ours.add(layer.id);
    const o: Record<string, unknown> = {};
    const paint: Record<string, unknown> = { ...(layer.paint ?? {}) };
    for (const p of Object.keys(lightOv.get(layer.id) ?? {})) if (!(p in paint)) paint[p] = undefined; // default in dark
    for (const [p, v] of Object.entries(paint)) {
      if (!tracked(layer.id, p)) continue;
      o[p] = v; const nv = tv(layer.id, p, v);
      if (nv === undefined) delete paint[p]; else paint[p] = nv;
    }
    orig.set(layer.id, o);
    return addLayer({ ...layer, paint } as LayerSpecification, before);
  }) as typeof map.addLayer;
  map.removeLayer = ((id: string) => { ours.delete(id); orig.delete(id); return removeLayer(id); }) as typeof map.removeLayer;
  map.addSource = ((id: string, src: Parameters<MlMap["addSource"]>[1]) => { oursSrc.add(id); return addSource(id, src); }) as typeof map.addSource;
  map.removeSource = ((id: string) => { oursSrc.delete(id); return removeSource(id); }) as typeof map.removeSource;
  map.setPaintProperty = ((id: string, p: string, v: unknown, opt?: Parameters<MlMap["setPaintProperty"]>[3]) => {
    if (ours.has(id) && tracked(id, p)) { (orig.get(id) ?? orig.set(id, {}).get(id)!)[p] = v; v = tv(id, p, v); }
    return setPaint(id, p, v, opt);
  }) as typeof map.setPaintProperty;
}

/** Re-colour our layers for the current theme from the stored dark originals. */
function recolor(map: MlMap): void {
  const setPaint = Object.getPrototypeOf(map).setPaintProperty as MlMap["setPaintProperty"];
  for (const [id, o] of orig) {
    if (!map.getLayer(id)) continue;
    for (const [p, v] of Object.entries(o)) { try { setPaint.call(map, id, p, tv(id, p, v) as never); } catch { /* prop not on this layer type */ } }
  }
}

/** Basemap swap keeping our sources / layers (same order: ours that sat under the basemap labels go
 *  under the new style's first label layer, the rest stay on top). */
function swapBasemap(map: MlMap, t: Theme): void {
  map.setStyle(basemapFor(t), {
    diff: true,
    transformStyle: (prev: StyleSpecification | undefined, next: StyleSpecification): StyleSpecification => {
      if (!prev) return next;
      const sources = { ...next.sources };
      for (const id of oursSrc) if (prev.sources[id]) sources[id] = prev.sources[id]!;
      const base = next.layers.slice();
      const firstSym = base.findIndex((l) => l.type === "symbol");
      const under: LayerSpecification[] = [], top: LayerSpecification[] = [];
      let seenBaseAfter = false;
      for (let i = prev.layers.length - 1; i >= 0; i--) { // walk from the top: ours above every basemap layer → "top"
        const l = prev.layers[i]!;
        if (!ours.has(l.id)) { seenBaseAfter = true; continue; }
        (seenBaseAfter ? under : top).unshift(l);
      }
      const at = firstSym < 0 ? base.length : firstSym;
      return { ...next, sources, layers: [...base.slice(0, at), ...under, ...base.slice(at), ...top] };
    },
  });
}

/** Switch instantly: page colours, basemap, our layers, our icons. */
export function setTheme(t: Theme, save = true): void {
  if (save) { try { localStorage.setItem(KEY, t); } catch { /* private mode */ } }
  if (t === theme) return;
  theme = t;
  if (t === "light") ensureLightCss();
  applyDocument(t);
  themeInlineTree(document.body);
  const map = mapRef;
  if (map) {
    recolor(map);
    swapBasemap(map, t);
    // a full style reload (diff refused) would drop our images → modules re-add them on this event too
    const again = (): void => { window.dispatchEvent(new Event("fs-theme-icons")); };
    map.once("styledata", again); window.setTimeout(again, 1200);
  }
  window.dispatchEvent(new Event("fs-theme"));
}
export const toggleTheme = (): void => setTheme(theme === "light" ? "dark" : "light");

/** Canvas icon helper: (re)draw an image for the current theme — add once, then update in place. */
export function putImage(map: MlMap, id: string, img: ImageData | { width: number; height: number; data: Uint8Array }, opts?: { pixelRatio?: number }): void {
  try {
    if (map.hasImage(id)) map.updateImage(id, img as ImageData);
    else map.addImage(id, img as ImageData, opts);
  } catch { /* style not ready */ }
}
/** Run `redraw` now and on every theme switch (and after a full style reload). */
export function onThemeIcons(redraw: () => void): void {
  window.addEventListener("fs-theme", redraw);
  window.addEventListener("fs-theme-icons", redraw);
}
